import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { NavLink } from "react-router-dom";
import { Button, Dropdown, Form, FormProps, Input, Modal, Segment, Table, TextArea } from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { hostAbsoluteUrl, restRequest } from "../http";
import { PageData } from "../models/common_model";
import { SVNRepositoryData } from "../models/svn_repository_model";
import { DropdownOption } from "../models/testing_model";
import { fieldError, formatDateTime } from "../utils";
import { Page } from "./commons/Page";

export function SvnRepositoryComponent(props: {}) {
    const currentUser = useCurrentUser();

    const [searchData, setSearchField] = useFormData<any>({});
    const [svnRepositories, setSearchData] = useFormConnect<any, PageData<SVNRepositoryData>>({}, 'svn_repository/search');

    const editModalControl = useModalControl();
    const [currentEditId, setCurrentEditId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);

    const [currentTestingProject, setCurrentTestingProject] = useState<SVNRepositoryData>();
    const testingModalControl = useModalControl();

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData(searchData)}>
                    <Form.Group>
                        <Form.Field inline>
                            <Input placeholder="Name" name="name" value={searchData['name'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <div><Button icon="search" type="submit"></Button></div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>URL</th>
                            <th>Results</th>
                            <th>Is Working</th>
                            <th>Use Folder Structure</th>
                            <th>Late Rivision</th>
                            <th>User Name</th>
                            <th>Created By</th>
                            <th>Created Time</th>
                            <th>{currentUser.permissions.svn_repository.create && <Button color="blue" icon="add" content="SVN Repository" size="mini" onClick={e => {
                                setCurrentEditId(undefined);
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {svnRepositories?.items.map(repository => {
                            return <tr key={repository.id}>
                                <td>{repository.url}</td>
                                <td>
                                    {currentUser.permissions.testing.view && repository.result_dropdown_options && repository.result_dropdown_options.length > 0 &&
                                        <Dropdown button className="mini" compact text="results">
                                            <Dropdown.Menu>
                                                {repository.result_dropdown_options.map(p => <Dropdown.Item key={p.id} className="project-menu-item">
                                                    <Dropdown text={p.name}>
                                                        <Dropdown.Menu>
                                                            {p.children.map(s => <Dropdown.Item key={s.id} className="project-menu-item">
                                                                <Dropdown text={s.name}>
                                                                    <Dropdown.Menu>
                                                                        {s.children.map(o => <Dropdown.Item key={o.id} as={NavLink} target="_blank" to={`/testing_visual/${o.id}/overview`}>{o.name}</Dropdown.Item>)}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            </Dropdown.Item>)}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </Dropdown.Item>)}
                                            </Dropdown.Menu>
                                        </Dropdown>}
                                </td>
                                <td>{repository.working ? 'Yes' : 'No'}</td>
                                <td>{repository.use_folder_structure ? 'Yes' : 'No'}</td>
                                <td>r{repository.last_revision}</td>
                                <td>{repository.username}</td>
                                <td>{repository.author.name}</td>
                                <td>{formatDateTime(repository.created_at)}</td>
                                <td>
                                    {currentUser.permissions.svn_repository.update && <Button onClick={e => {
                                        setCurrentEditId(repository.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" />}

                                    {currentUser.permissions.svn_repository.clean && <Button onClick={e => {
                                        dialogControl.confirm('Are you sure to clean the result data?', agree => {
                                            if (agree) {
                                                return restRequest(`svn_repository/clean/${repository.id}`, null, 'get').then(() => {
                                                    setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
                                                })
                                            }
                                            return Promise.resolve()
                                        });
                                    }} color="pink" size="mini" >Clean</Button>}

                                    {currentUser.permissions.svn_repository.delete && <Button onClick={e => {
                                        dialogControl.deletionConfirm(repository.url, argee => {
                                            if (argee) {
                                                return restRequest(`svn_repository/${repository.id}`, null, 'delete').then(() => {
                                                    setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
                                                })
                                            }
                                            return Promise.resolve()
                                        })
                                    }} color="red" icon="delete" size="mini" />}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={svnRepositories} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor id={currentEditId} modalControl={editModalControl} onFinish={result => {
            setSearchData({ ...searchData, refresh: (searchData.refresh || 1) + 1 });
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }}></Editor>
    </>
}

function Editor(props: { id?: number, modalControl: ModalControlData, onFinish: (data: SVNRepositoryData) => void }) {
    const { id, modalControl, onFinish } = props;
    const isCreate = !id;

    const [data, setField, setData] = useFormData<SVNRepositoryData>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setData({} as any);
        if (id) {
            setLoading(true);
            restRequest<SVNRepositoryData>('svn_repository/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<SVNRepositoryData>(`svn_repository/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }


    return <>
        <Modal {...modalControl.props} size="large">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} Project </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>

                    <Form.Group widths="equal">
                        <Form.Input label="Url" name="url" value={data.url || ''} onChange={setField} required error={fieldError('url', invalidError)} disabled={!isCreate}>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="User Name" name="username" value={data.username || ''} onChange={setField} required error={fieldError('username', invalidError)}>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Password" type="password" name="password" value={data.password || ''} onChange={setField} required={isCreate} error={fieldError('password', invalidError)}>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Checkbox label="Is Working" name="working" checked={data.working || false} onClick={e => setField(null, { name: 'working', value: !data.working || false })}></Form.Checkbox>
                        <Form.Checkbox label="Use Folder Structure" name="use_folder_structure" checked={data.use_folder_structure || false} onClick={e => setField(null, { name: 'use_folder_structure', value: !data.use_folder_structure || false })}></Form.Checkbox>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Description" error={fieldError('description', invalidError)}>
                            <TextArea name="description" value={data.description || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>
                    <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modalControl.setOpen(false)}>Cancel</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>
    </>
}